import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { layersApi, type ReportTable } from '../../lib/layersApi';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** «Продемонстрировать формирование отчёта по инвентаризации по выбранному
 * географическому объекту с выгрузкой в Excel» — выбранный объект здесь это
 * буквально тот, чью карточку сейчас открыли; отчёт — сам объект плюс всё,
 * что на нём/у него по иерархии (см. HierarchyModal), в одну плоскую
 * таблицу. Показывает предпросмотр перед скачиванием, а не скачивает вслепую. */
export function InventoryReportModal({
  layerId,
  featureId,
  onClose,
}: {
  layerId: string;
  featureId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<(ReportTable & { title: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    layersApi
      .getFeatureInventoryReport(layerId, featureId)
      .then(setData)
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Не удалось построить отчёт.'))
      .finally(() => setLoading(false));
  }, [layerId, featureId]);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await layersApi.exportFeatureInventoryReportExcel(layerId, featureId);
      downloadBlob(blob, `Инвентаризация — ${data?.title ?? 'объект'}.xlsx`);
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : 'Не удалось экспортировать отчёт.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/52 p-6" onClick={onClose}>
      <div
        className="flex max-h-[80vh] w-full max-w-[820px] flex-col overflow-hidden rounded-2xl border border-border-default bg-bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border-default px-5 py-3.5">
          <h2 className="text-[15px] font-semibold text-text-primary">Отчёт по инвентаризации</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || loading || !data}
              className="flex h-9 items-center rounded-lg bg-action-primary px-3 text-[12px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? 'Экспортируем…' : 'Скачать Excel'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex size-8 items-center justify-center rounded-lg text-lg text-text-secondary hover:bg-bg-surface-secondary"
            >
              ×
            </button>
          </div>
        </div>

        <div className="overflow-auto px-5 py-4">
          {loading && <p className="text-[13px] text-text-secondary">Загрузка…</p>}
          {loadError && <p className="text-[13px] font-medium text-status-danger">{loadError}</p>}
          {exportError && <p className="mb-2 text-[12px] font-medium text-status-danger">{exportError}</p>}
          {data && (
            <table className="w-full border-collapse text-left text-xs">
              <thead className="bg-bg-surface-secondary">
                <tr>
                  {data.columns.map((c) => (
                    <th key={c} className="whitespace-nowrap px-3 py-2 font-semibold text-text-secondary">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-b border-border-default align-top">
                    {row.map((cell, j) => (
                      <td key={j} className="px-3 py-1.5 text-text-primary">
                        {cell ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
